import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiCall } from '../api/client';

export default function CreateCourse() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const onBack = () => navigate('/');
  // Replace so Back from the editor doesn't return to the spent creation form.
  const onCourseCreated = (courseId) => navigate(`/courses/${courseId}/edit`, { replace: true });
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [officialDbs, setOfficialDbs] = useState([]);
  const [selectedDbId, setSelectedDbId] = useState('');
  const [columns, setColumns] = useState(['', '']);
  const [columnTypes, setColumnTypes] = useState(['text', 'text']);
  const [loadingDbs, setLoadingDbs] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiCall('GET', '/databases/official', null, token)
      .then(data => setOfficialDbs(data))
      .catch(() => setOfficialDbs([]))
      .finally(() => setLoadingDbs(false));
  }, [token]);

  const selectedDb = officialDbs.find(db => db.id === selectedDbId);
  const isOther = selectedDbId === 'other';

  const hasAudioColumn = columnTypes.includes('audio');

  function updateColumn(i, value) {
    setColumns(cols => cols.map((c, idx) => (idx === i ? value : c)));
  }
  function updateColumnType(i, type) {
    setColumnTypes(types => types.map((t, idx) => (idx === i ? type : t)));
  }
  function addColumn() {
    if (columns.length >= 10) return;
    setColumns(cols => [...cols, '']);
    setColumnTypes(types => [...types, 'text']);
  }
  function removeColumn(i) {
    if (columns.length <= 2) return;
    setColumns(cols => cols.filter((_, idx) => idx !== i));
    setColumnTypes(types => types.filter((_, idx) => idx !== i));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');

    if (!title.trim()) {
      setError('Course title is required.');
      return;
    }
    if (!selectedDbId) {
      setError('Please choose a language.');
      return;
    }
    const cleanedColumns = columns.map(c => c.trim());
    if (isOther && (cleanedColumns.length < 2 || cleanedColumns.some(c => !c))) {
      setError('Please enter a name for each column (at least 2).');
      return;
    }

    setSubmitting(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        databaseId: isOther ? null : selectedDbId,
      };
      if (isOther) {
        body.columns = cleanedColumns;
        body.columnTypes = columnTypes;
      }
      const course = await apiCall('POST', '/courses', body, token);
      onCourseCreated(course.id);
    } catch (err) {
      setError(err.message || 'Failed to create course.');
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-amber-50">
      <header className="bg-white border-b border-amber-100 px-4 sm:px-6 py-4 flex items-center gap-3 sm:gap-4 sticky top-0 z-10">
        <button
          onClick={onBack}
          className="text-amber-600 hover:text-amber-800 font-medium text-sm flex-shrink-0"
        >
          ← Back
        </button>
        <h1 className="text-lg sm:text-xl font-bold text-amber-800 truncate">Create a New Course</h1>
      </header>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Course details */}
          <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold text-amber-800">Course Details</h2>

            <div>
              <label className="block text-sm font-medium text-amber-700 mb-1">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="e.g. French for Beginners"
                className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-amber-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="What will learners study in this course?"
                rows={3}
                className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 resize-none"
              />
            </div>
          </div>

          {/* Language */}
          <div className="bg-white rounded-xl border border-amber-100 shadow-sm p-5 sm:p-6 space-y-4">
            <h2 className="font-semibold text-amber-800">Language</h2>

            {loadingDbs ? (
              <p className="text-amber-500 text-sm">Loading languages...</p>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-1">
                    Language database <span className="text-red-400">*</span>
                  </label>
                  <select
                    value={selectedDbId}
                    onChange={e => setSelectedDbId(e.target.value)}
                    className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                  >
                    <option value="">Select a language...</option>
                    {officialDbs.map(db => (
                      <option key={db.id} value={db.id}>{db.name}</option>
                    ))}
                    <option value="other">Other</option>
                  </select>
                </div>

                {selectedDb && (
                  <div className="bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 text-sm text-amber-700">
                    <span className="font-medium">Columns: </span>
                    {selectedDb.columns.join(', ')}
                  </div>
                )}

                {isOther && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-amber-700 mb-1">
                        Columns <span className="text-red-400">*</span>
                      </label>
                      <p className="text-xs text-amber-500 mb-2">
                        Add the fields each word has (e.g. Spanish word, English translation, Example sentence).
                        You'll choose which columns are the question and answer for each level. Up to one column
                        can be audio — you'll upload clips when adding words.
                      </p>
                      <div className="space-y-2">
                        {columns.map((col, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={col}
                              onChange={e => updateColumn(i, e.target.value)}
                              placeholder={`Column ${i + 1}`}
                              className="flex-1 border border-amber-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                            />
                            <select
                              value={columnTypes[i]}
                              onChange={e => updateColumnType(i, e.target.value)}
                              className="border border-amber-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 bg-white"
                            >
                              <option value="text">Text</option>
                              <option value="audio" disabled={hasAudioColumn && columnTypes[i] !== 'audio'}>Audio</option>
                            </select>
                            <button
                              type="button"
                              onClick={() => removeColumn(i)}
                              disabled={columns.length <= 2}
                              title="Remove column"
                              aria-label="Remove column"
                              className="text-amber-300 hover:text-red-500 disabled:opacity-30 disabled:cursor-not-allowed transition text-lg leading-none px-1"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                    {columns.length < 10 && (
                      <button
                        type="button"
                        onClick={addColumn}
                        className="text-sm font-medium text-amber-600 hover:text-amber-800 transition"
                      >
                        + Add column
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <p className="text-red-500 text-sm px-1">{error}</p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-amber-500 hover:bg-amber-600 disabled:bg-amber-300 text-white font-semibold py-3 rounded-xl transition"
          >
            {submitting ? 'Creating...' : 'Create Course'}
          </button>
        </form>
      </div>
    </div>
  );
}
